/*
 * Product upsell checkboxes (GrillDaddy custom)
 *
 * Behaviour:
 *  - Keeps a live "order total" in sync with the main variant price + any ticked add-ons.
 *  - On add-to-cart, the ticked add-on products are added to the cart *first* via a silent
 *    /cart/add.js call, then the form is re-submitted so Minimog's own handler adds the main
 *    product and renders the cart drawer (which now shows everything together).
 *
 * Notes:
 *  - Dynamic checkout buttons (Buy it now / PayPal / Apple Pay) bypass the product form submit,
 *    so add-ons are not captured for those express paths. The standard "Add to cart" button works.
 */
(function () {
  if (window.customElements && window.customElements.get('m-product-upsell')) return;

  var moneyFallback = function (cents) {
    return '$' + (Math.round(cents) / 100).toFixed(2);
  };

  function money(cents, format) {
    if (typeof window.formatMoney === 'function') {
      try {
        return window.formatMoney(cents, format);
      } catch (e) {
        /* fall through */
      }
    }
    return moneyFallback(cents);
  }

  function cartAddUrl() {
    if (window.MinimogSettings && window.MinimogSettings.routes && window.MinimogSettings.routes.cart_add_url) {
      return window.MinimogSettings.routes.cart_add_url;
    }
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/$/, '') + '/cart/add.js';
  }

  class ProductUpsell extends HTMLElement {
    constructor() {
      super();
      this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
      this.handleFormChange = this.handleFormChange.bind(this);
    }

    connectedCallback() {
      this.scope = this.closest('.m-main-product--info') || document;
      this.productForm = this.closest('product-form') || this.scope.querySelector('product-form');
      this.form = this.productForm ? this.productForm.querySelector('form') : null;

      this.checkboxes = Array.prototype.slice.call(this.querySelectorAll('[data-upsell-variant]'));
      this.totalEl = this.querySelector('[data-upsell-total]');
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;

      var dataEl = document.getElementById('productData');
      if (dataEl) {
        try {
          this.productData = JSON.parse(dataEl.textContent);
        } catch (e) {
          this.productData = null;
        }
      }

      this.checkboxes.forEach(
        function (cb) {
          cb.addEventListener('change', this.handleCheckboxChange);
        }.bind(this)
      );

      if (this.form) {
        this.form.addEventListener('change', this.handleFormChange);
        this.bindSubmit();
      }

      this.updateTotal();
    }

    disconnectedCallback() {
      this.checkboxes.forEach(
        function (cb) {
          cb.removeEventListener('change', this.handleCheckboxChange);
        }.bind(this)
      );
      if (this.form) this.form.removeEventListener('change', this.handleFormChange);
    }

    get idInput() {
      return this.form ? this.form.querySelector('[name="id"]') : null;
    }

    handleCheckboxChange() {
      this.updateTotal();
    }

    handleFormChange(event) {
      // Ignore our own checkboxes; only react to the main variant changing.
      if (event.target && event.target.hasAttribute && event.target.hasAttribute('data-upsell-variant')) return;
      var self = this;
      window.requestAnimationFrame(function () {
        var input = self.idInput;
        if (!input || !self.productData || !self.productData.variants) return;
        var id = parseInt(input.value, 10);
        var variant = self.productData.variants.filter(function (v) {
          return v.id === id;
        })[0];
        if (variant && typeof variant.price === 'number') {
          self.basePrice = variant.price;
          self.updateTotal();
        }
      });
    }

    selectedAddons() {
      return this.checkboxes.filter(function (cb) {
        return cb.checked && !cb.disabled;
      });
    }

    updateTotal() {
      if (!this.totalEl) return;
      var addonsCents = this.selectedAddons().reduce(function (sum, cb) {
        return sum + (parseInt(cb.dataset.price, 10) || 0);
      }, 0);
      this.totalEl.innerHTML = money(this.basePrice + addonsCents, this.moneyFormat);
    }

    bindSubmit() {
      if (this.form.__upsellBound) return;
      this.form.__upsellBound = true;

      var form = this.form;
      var self = this;

      // Listen on document in the capture phase: this runs BEFORE any listener
      // registered on the form itself (the theme's product-form handler), which
      // is the only reliable way to win since target-phase listeners on the form
      // fire in registration order regardless of the capture flag.
      document.addEventListener(
        'submit',
        function (event) {
          if (event.target !== form) return;
          // Second pass (re-submit) — let Minimog's own handler run.
          if (form.__upsellPassthrough) {
            form.__upsellPassthrough = false;
            return;
          }
          var items = self.collectAllChecked();
          if (!items.length) return; // no add-ons: normal theme flow

          event.preventDefault();
          event.stopPropagation(); // keep the event from ever reaching the theme's handler
          self.addAddonsThenSubmit(items);
        },
        true
      );
    }

    collectAllChecked() {
      var nodes = Array.prototype.slice.call(this.scope.querySelectorAll('[data-upsell-variant]:checked'));
      return nodes
        .filter(function (cb) {
          return !cb.disabled;
        })
        .map(function (cb) {
          return { id: parseInt(cb.value, 10), quantity: 1 };
        });
    }

    addAddonsThenSubmit(items) {
      var form = this.form;
      var finish = function () {
        form.__upsellPassthrough = true;
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
      };

      fetch(cartAddUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items: items }),
      })
        .then(function (res) {
          return res.json().catch(function () {
            return null;
          });
        })
        .catch(function (err) {
          console.error('[product-upsell] add-on add failed', err);
        })
        .finally(finish);
    }
  }

  window.customElements.define('m-product-upsell', ProductUpsell);
})();
